// Pre-configured validation regex strings (stored verbatim in field.validation)
// and helpers shared between the admin field editor and the public survey runtime.

export const VALIDATION_EMAIL_REGEX = '^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}$';
export const VALIDATION_PHONE_REGEX = '^[+]?[0-9 .()-]{7,}$';

// Matches common PO Box spellings: "PO Box", "P.O. Box", "P O Box", "Post Office Box", "POB 12", etc.
const PO_BOX_REGEX = /\b(?:p\.?\s*o\.?\s*[\-\.]?\s*b(?:ox)?|post\s*office\s*box)\b/i;

export const looksLikePoBox = (...parts) => {
    const text = parts.filter(Boolean).join(' ').trim();
    if (!text) return false;
    return PO_BOX_REGEX.test(text);
};

export const PO_BOX_ERROR_MESSAGE = 'PO Boxes are not allowed. Please enter a physical street address.';

export const escapeForCharClass = (s) => (s || '').replace(/[\\\]\^\-]/g, '\\$&');

// Build an "allowed-characters" regex from a parts object:
//   { uppercase, lowercase, digits, allowedChars }
// Spaces are always permitted. Any category set to false is excluded from the
// generated character class.
export const buildAllowedCharsRegex = (parts) => {
    const { uppercase = false, lowercase = false, digits = false, allowedChars = '' } = parts || {};
    let cls = '';
    if (uppercase) cls += 'A-Z';
    if (lowercase) cls += 'a-z';
    if (digits) cls += '0-9';
    cls += ' ';
    cls += escapeForCharClass(allowedChars);
    return `^[${cls}]*$`;
};

// Parse the body of a `[...]` character class back into the toggle parts.
// Returns null if the body uses anything beyond the supported simple tokens
// (A-Z, a-z, 0-9, space, single-char literals, and \-escaped specials).
const parseCharClassBody = (body) => {
    const result = { uppercase: false, lowercase: false, digits: false, allowedChars: '' };
    let i = 0;
    while (i < body.length) {
        if (body.substring(i, i + 3) === 'A-Z') { result.uppercase = true; i += 3; continue; }
        if (body.substring(i, i + 3) === 'a-z') { result.lowercase = true; i += 3; continue; }
        if (body.substring(i, i + 3) === '0-9') { result.digits = true; i += 3; continue; }
        if (body[i] === ' ') { i += 1; continue; }
        if (body[i] === '\\' && i + 1 < body.length) {
            result.allowedChars += body[i + 1];
            i += 2;
            continue;
        }
        const c = body[i];
        if ((c >= 'A' && c <= 'Z') || (c >= 'a' && c <= 'z') || (c >= '0' && c <= '9')) {
            return null;
        }
        result.allowedChars += c;
        i += 1;
    }
    return result;
};

export const detectValidationPreset = (validation) => {
    const defaults = { uppercase: false, lowercase: false, digits: false, allowedChars: '' };
    if (!validation) return { preset: 'none', ...defaults };
    if (validation === VALIDATION_EMAIL_REGEX) return { preset: 'email', ...defaults };
    if (validation === VALIDATION_PHONE_REGEX) return { preset: 'phone', ...defaults };
    const m = validation.match(/^\^\[(.+)\]\*\$$/);
    if (m) {
        const parts = parseCharClassBody(m[1]);
        if (parts) return { preset: 'allowedchars', ...parts };
    }
    return { preset: 'custom', ...defaults };
};

// Build a respondent-facing error message tailored to the validation rule on a
// survey field. Falls back to a generic message for custom regexes.
export const buildValidationErrorMessage = (field) => {
    const title = field?.title || 'This field';
    const detected = detectValidationPreset(field?.validation);
    switch (detected.preset) {
        case 'email':
            return `${title} must be a valid email address (e.g. name@example.com).`;
        case 'phone':
            return `${title} must be a valid phone number (digits, spaces, and . ( ) - + are allowed; at least 7 characters).`;
        case 'allowedchars': {
            const { uppercase, lowercase, digits, allowedChars } = detected;
            const parts = [];
            if (uppercase && lowercase) parts.push('letters');
            else if (uppercase) parts.push('uppercase letters (A-Z)');
            else if (lowercase) parts.push('lowercase letters (a-z)');
            if (digits) parts.push('digits');
            parts.push('spaces');
            let msg = `${title} may only contain ${parts.join(', ')}`;
            if (allowedChars) msg += `, and these characters: ${allowedChars}`;
            return msg + '.';
        }
        default:
            return `${title} is not in the correct format.`;
    }
};
